"use client";

import React from "react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { ShieldAlert, Activity, ArrowRight, MapPin, Syringe } from "lucide-react";
const mockMedicalRisks = [{
  label: "BALAYAGE",
  level: "Zachte Karamel",
  icon: Activity,
  color: "text-primary"
}, {
  label: "STYLING",
  level: "Volumineuze Blow-out",
  icon: ShieldAlert,
  color: "text-secondary"
}, {
  label: "KNIPPEN",
  level: "Scherpe Bob",
  icon: Syringe,
  color: "text-primary"
}, {
  label: "SNIJDEN",
  level: "Klassieke Herensnit",
  icon: Activity,
  color: "text-secondary"
}, {
  label: "BRUID",
  level: "Elegante Updo",
  icon: ShieldAlert,
  color: "text-primary"
}];
const bentoGridClasses = ["md:col-span-4 md:row-span-2", "md:col-span-2 md:row-span-1", "md:col-span-2 md:row-span-1", "md:col-span-3 md:row-span-1", "md:col-span-3 md:row-span-1"];
function BentoGridConsumer() {
  const {
    posts,
    loading,
    error
  } = usePosts();
  if (loading) {
    return <div data-section-id="95967" className="grid grid-cols-1 md:grid-cols-6 auto-rows-[300px] gap-4 md:gap-6">
        {[...Array(5)].map((_, i) => <div key={i} data-index={i} className={`rounded-xl bg-muted/30 animate-pulse ${bentoGridClasses[i] || "md:col-span-2 md:row-span-1"}`} />)}
      </div>;
  }
  if (error) {
    return <div className="flex flex-col items-center justify-center py-12 text-center">
        <ShieldAlert className="w-12 h-12 text-destructive mb-4 opacity-50" />
        <p className="text-muted-foreground">Kon de creaties niet laden. Probeer het later opnieuw.</p>
      </div>;
  }
  if (!posts || posts.length === 0) {
    return <div className="flex flex-col items-center justify-center py-12 text-center">
        <MapPin className="w-12 h-12 text-muted-foreground mb-4 opacity-50" />
        <p className="text-muted-foreground">Er zijn momenteel geen creaties beschikbaar om te tonen.</p>
      </div>;
  }
  const displayPosts = posts.slice(0, 5);
  return <div className="grid grid-cols-1 md:grid-cols-6 auto-rows-[300px] md:auto-rows-[340px] gap-4 md:gap-6">
      {displayPosts.map((post, i) => {
      const gridClass = bentoGridClasses[i] || "md:col-span-2 md:row-span-1";
      const risk = mockMedicalRisks[i % mockMedicalRisks.length];
      const RiskIcon = risk.icon;
      const category = post.categories?.[0]?.name || "Behandeling";
      const imageUrl = post.featuredImage || `/img/generic_travel_destination_${i + 1}.png`;
      return <Card key={post.id} data-index={i} className={`group relative overflow-hidden rounded-xl border-0 shadow-sm hover:shadow-lg transition-shadow duration-500 p-0 ${gridClass}`}>
            <img src={imageUrl} alt={post.title} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-110" />

            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent opacity-100 group-hover:opacity-0 transition-opacity duration-500" />
            
            <div className="absolute top-5 left-5 flex gap-2 z-10">
              <Badge variant="secondary" className="bg-background/90 backdrop-blur-sm border-none text-foreground font-medium tracking-widest uppercase text-[10px] px-3 py-1">
                {category}
              </Badge>
            </div>

            <div className="absolute inset-x-0 bottom-0 p-6 translate-y-0 group-hover:translate-y-8 group-hover:opacity-0 transition-all duration-500 ease-in-out z-10">
              <h3 className="text-2xl md:text-3xl font-serif font-bold text-white leading-tight">
                {post.title}
              </h3>
            </div>

            <div className="absolute inset-x-0 bottom-0 translate-y-full group-hover:translate-y-0 transition-transform duration-500 ease-out bg-background/95 backdrop-blur-md border-t border-border p-6 md:p-8 flex flex-col gap-4 z-20 h-auto max-h-full overflow-y-auto">
              <h3 className="text-xl md:text-2xl font-serif font-bold text-foreground leading-tight">
                {post.title}
              </h3>
              
              <div className="flex items-center gap-2 bg-accent/50 p-3 rounded-lg border border-border">
                <RiskIcon className={`w-5 h-5 ${risk.color}`} />
                <div className="flex flex-col">
                  <span className="text-xs text-muted-foreground font-medium uppercase tracking-wider">{risk.label}</span>
                  <span className={`text-sm font-bold ${risk.color}`}>{risk.level}</span>
                </div>
              </div>

              {post.excerpt && <p className="text-muted-foreground text-sm line-clamp-2 md:line-clamp-3 leading-relaxed font-sans">
                  {post.excerpt}
                </p>}

              <div className="mt-auto pt-2">
                <Link to={post.link || "#"} className="inline-block w-full">
                  <Button variant="default" className="w-full rounded-md bg-primary text-white hover:bg-primary/90 group/btn uppercase tracking-widest text-xs font-bold">
                    Bekijk Resultaat 
                    <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover/btn:translate-x-1" />
                  </Button>
                </Link>
              </div>
            </div>
          </Card>;
    })}
    </div>;
}
export default function MedischReisadvies() {
  const wp_query_params = {
    per_page: 5,
    orderby: "date",
    order: "desc"
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section id="onze-behandelingen" className="py-24 md:py-32 bg-background relative overflow-hidden">
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none z-0">
        <div className="absolute -top-[20%] -right-[10%] w-[50%] h-[50%] rounded-full bg-primary/5 blur-[120px]" />
        <div className="absolute bottom-[10%] -left-[10%] w-[40%] h-[40%] rounded-full bg-accent/30 blur-[100px]" />
      </div>

      <div className="container mx-auto px-4 md:px-8 relative z-10">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16 md:mb-20">
          <div className="max-w-2xl">
            <Badge variant="outline" className="mb-4 border-primary/30 text-primary bg-primary/5 uppercase tracking-widest text-[10px] px-4 py-1">
              Inspiratie & Creaties
            </Badge>
            <h2 className="text-4xl md:text-6xl font-serif font-bold text-foreground mb-6 tracking-tighter">
              Onze Recente <br className="hidden md:block" />
              <span className="text-primary italic">Transformaties</span>
            </h2>
            <p className="text-lg text-muted-foreground leading-relaxed font-sans">
              Scroll door onze favoriete creaties en laat je inspireren. Van subtiele kleuringen tot complete metamorfoses, uitgevoerd met passie en oog voor detail.
            </p>
          </div>
          <div className="shrink-0">
            <Link to="#" className="inline-block">
              <Button variant="outline" className="rounded-md border-primary text-primary hover:bg-primary hover:text-white px-8 py-6 uppercase tracking-widest text-xs font-bold transition-colors">
                Bekijk Volledig Portfolio
              </Button>
            </Link>
          </div>
        </div>

        <PostsProvider wp_query={{
        per_page: 5,
        orderby: "date",
        order: "desc"
      }}>
          <BentoGridConsumer />
        </PostsProvider>
      </div>
    </section>;
}